import { useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import MoreVertIcon from "@material-ui/icons/MoreVertOutlined";
import { Box, Button, Menu, MenuItem, Typography } from "@material-ui/core";
import {
  EventItem,
  LegacyRoleUpdatedData,
  LegacyRuleDeletedData,
  LegacyRuleUpdatedData,
} from "@dashboard-v3/api";
import downloadFile from "utils/downloadFile";
import EventDetails from "./EventDetails";

interface ItemMenuProps {
  item: EventItem;
}

type Download = { content: unknown; name: string };

export default function ItemMenu({ item }: ItemMenuProps) {
  const { t } = useTranslation(["userActivity", "users"]);
  const [anchor, setAnchor] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const options = getMenuOptions(item);

  const onButtonClick: React.MouseEventHandler = e => {
    const { target } = e;
    e.stopPropagation();
    setAnchor(target);
  };

  const onMenuClose = e => {
    e.stopPropagation();
    setAnchor(null);
  };

  const onSelect = (value: string) => () => {
    const download = resolveDownload(item, value, t);
    if (download) downloadFile(download.content, download.name, "json");
    setAnchor(null);
  };

  return (
    <Box paddingRight={options.length ? "0" : "1rem"}>
      <Button
        size="small"
        color="primary"
        data-testid="user-activity-item__details-btn"
        onClick={e => {
          e.stopPropagation();
          setShowDetails(true);
        }}
      >
        {t("eventItem.details")}
      </Button>
      {options.length > 0 && (
        <>
          <Button
            size="small"
            style={{ backgroundColor: "transparent" }}
            disableFocusRipple
            disableRipple
            data-testid="user-activity-item__menu-btn"
            onClick={onButtonClick}
          >
            <MoreVertIcon />
          </Button>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={onMenuClose}
            getContentAnchorEl={null}
            keepMounted
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            {options.map(option => (
              <MenuItem key={option} value={option} onClick={onSelect(option)}>
                <Typography variant="caption">
                  {t(`eventItem.menu.${option}`)}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      {showDetails && (
        <EventDetails item={item} onClose={() => setShowDetails(false)} />
      )}
    </Box>
  );
}

function getMenuOptions(item: EventItem): string[] {
  const { event } = item;

  if (event.type === "rule_deleted") {
    return ["DOWNLOAD_REMOVED"];
  }

  if (event.type === "rule_updated") {
    // TODO(MXH-5745): legacy events carry a diff and no `previous` snapshot.
    if (isLegacyPayload<LegacyRuleUpdatedData>(event.data, "currentRule")) {
      return ["DOWNLOAD_UPDATED", "DOWNLOAD_DIFF"];
    }
    return ["DOWNLOAD_UPDATED", "DOWNLOAD_PREVIOUS"];
  }

  if (event.type === "role_updated") {
    return ["DOWNLOAD_UPDATED_PERMISSIONS"];
  }

  return [];
}

function resolveDownload(
  item: EventItem,
  option: string,
  t: TFunction<"userActivity" | "users">
): Download | null {
  const { event } = item;

  if (event.type === "rule_deleted") {
    // TODO(MXH-5745): legacy events wrap the snapshot in `deletedRule`.
    const rule = isLegacyPayload<LegacyRuleDeletedData>(
      event.data,
      "deletedRule"
    )
      ? event.data.deletedRule
      : event.data;

    return { content: rule, name: `${event.data.name}-DELETED` };
  }

  if (event.type === "rule_updated") {
    const { name } = event.data;

    // TODO(MXH-5745): legacy branch — `currentRule` + `changes` instead of
    // `previous` + `current`.
    if (isLegacyPayload<LegacyRuleUpdatedData>(event.data, "currentRule")) {
      if (option === "DOWNLOAD_DIFF") {
        return { content: event.data.changes, name: `${name}-DIFF` };
      }
      return { content: event.data.currentRule, name: `${name}-UPDATED` };
    }

    if (option === "DOWNLOAD_PREVIOUS") {
      return { content: event.data.previous, name: `${name}-PREVIOUS` };
    }
    return { content: event.data.current, name: `${name}-UPDATED` };
  }

  if (event.type === "role_updated") {
    const roleName = t(`users:userRole.${event.data.role}`);

    // TODO(MXH-5745): legacy events only stored the resulting permissions.
    const permissions = isLegacyPayload<LegacyRoleUpdatedData>(
      event.data,
      "permissions"
    )
      ? event.data.permissions
      : event.data.current;

    return { content: permissions, name: `${roleName}-update` };
  }

  return null;
}

/* ==========================================================================
 * ⚠️  LEGACY EVENT SUPPORT — DELETE THIS BLOCK AFTER 2027-08-01
 * ==========================================================================
 *
 * Same situation as the LEGACY block in `Affected.tsx`: events written before
 * MXH-5745 cannot be migrated, only outlived. The last copies expire 12 months
 * after the release (sprint 177, 2026-08).
 *
 * To remove: delete this block and every branch marked `TODO(MXH-5745)` above,
 * keeping the non-legacy side. `DOWNLOAD_DIFF` goes away with them — the diff
 * is derivable from `previous` + `current`, which is why it is no longer
 * persisted.
 *
 * @see api/src/types/eventsLegacy.ts
 * @see api/MXH-5745-EVENTS-DECISION-TABLE.md §6.8
 * ======================================================================== */

/** LEGACY. Discriminates an old payload by a key only the old shape carries. */
function isLegacyPayload<T>(data: unknown, key: string): data is T {
  return typeof data === "object" && data !== null && key in data;
}
